// ============================================================
// AUTH CONTEXT (P2) — provides the current viewer (Member) + auth controls.
//
// Two modes, chosen at runtime:
//   • LIVE   — Firebase が設定済み（isFirebaseConfigured）かつ本番（import.meta.env.PROD）
//              または明示的に有効化された場合。Google ログイン（signInWithPopup）
//              ＋ onAuthStateChanged。ログインユーザーの email から Firestore
//              `members` を引き当てて viewer（role/exec/projects）を確定する。
//   • DEV    — Firebase 未設定、または開発ビルド。demoData の MEMBERS から
//              閲覧ユーザーを切り替えられる（dev viewer-switch / VIEWERS）。
//
// 既存コンポーネント（Sidebar/TopBar/screens）は `viewer`（非null）/`setViewer`/
// `switchable` をそのまま使う。LIVE 未ログイン時は App.tsx が <Login/> を出し、
// シェル内に入った時点で viewer は必ず確定している（このAPI契約を維持）。
// ============================================================
import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import {
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithPopup,
  signOut as fbSignOut,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions, isFirebaseConfigured } from '../lib/firebase';
import { getAdapter, setActiveScope } from '../data/adapter';
import { MEMBERS, MEMBER_MAP, VIEWERS } from '../data/demoData';
import type { Member } from '../types/models';

/**
 * viewer から firebaseAdapter の絞り込み scope を導出して登録する（P4）。
 * admin/exec は crossRead=true（全読み）。一般メンバーは自分担当タスク・権限PJ議事録に絞る。
 * null（ログアウト/未確定）でクリア。mockAdapter は scope を無視するので DEV は無影響。
 */
function applyScope(member: Member | null): void {
  setActiveScope(
    member
      ? {
          memberId: member.id,
          projects: member.projects || [],
          crossRead: member.role === 'admin' || !!member.exec,
        }
      : null,
  );
}

export type AuthMode = 'dev' | 'live';

export interface AuthState {
  /** 現在の閲覧ユーザー（権限フィルタの起点）。LIVE 未ログイン時のみ null。 */
  viewer: Member | null;
  /**
   * 現在ログイン中ユーザーの一意キー（Firestore オーバーレイ等の所有者識別に使う）。
   * LIVE は Firebase Auth の uid、DEV は viewer.id。未ログインは null。
   */
  uid: string | null;
  /** dev viewer-switch（DEV モードのみ機能・LIVE では no-op）。 */
  setViewer: (memberId: string) => void;
  /** dev の切替候補（DEV モードのみ表示）。 */
  switchable: Member[];

  /** 'dev'（切替UI）/ 'live'（Googleログイン）。 */
  mode: AuthMode;
  /** 初期化中（onAuthStateChanged の最初の確定待ち）。 */
  loading: boolean;
  /** LIVE: Google でログイン中だが members に未登録の email（ガード表示用）。 */
  unregisteredEmail: string | null;
  /**
   * LIVE: Google OAuth アクセストークン（Calendar/Tasks/Docs API 呼び出し用）。
   *
   * 取得の優先順位（2026-07-10 サーバー発行方式に転換）:
   *   1) sessionStorage キャッシュ（有効期限内のみ。リロード直後の復元）
   *   2) callable `getGoogleAccessToken` — B2 常時同期でサーバーに預かっている本人の
   *      refresh_token から access_token を発行して返す。**ポップアップ不要**なので
   *      タブ跨ぎ・リロードでも黙って復元できる（GIS サイレント再取得はジェスチャ無し
   *      ポップアップが常にブロックされ構造的に機能しないため廃止・削除済み）。
   *   3) 手動 signInWithPopup（「連携」ボタン。初回同意・refresh_token 未預託ユーザー用）
   *
   * 2) が needsConsent（B2 未許可／旧 scope／失効）や失敗のときは null のまま
   * ＝従来どおり未連携表示→手動 signInWithGoogle にフォールバックする。
   * ⚠️ B2（常時同期）を一度も許可していないユーザーはサーバーに refresh_token が無いので
   *    2) は常に needsConsent → 「タブごとに連携ボタンを押す」従来体験のままになる
   *    （MyTasks の「常時同期を有効にする」ボタンで B2 を許可すると 2) が使えるようになる）。
   *
   * トークンは短命（〜1時間）だが、2) はいつでも呼べるため失効時はサーバーから自動で
   * 取り直す（LiveAuthProvider の失効タイマー参照）。DEV モードでは常に null。
   */
  googleAccessToken: string | null;
  /** LIVE: Google ログインを開始。 */
  signInWithGoogle: () => Promise<void>;
  /** LIVE: ログアウト（DEV では no-op）。 */
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

// ── dev 切替候補（VIEWERS → Member 解決・欠落は除外）──
const SWITCHABLE: Member[] = (VIEWERS.map((id) => MEMBER_MAP[id]).filter(Boolean) as Member[]) || [];
const DEFAULT_DEV_VIEWER: Member = SWITCHABLE[0] || MEMBERS[0];

/**
 * LIVE モードを使うか。Firebase が設定済みで、本番ビルド（PROD）の場合のみ実ログイン。
 * 開発ビルド（DEV）は Firebase 設定があっても dev 切替を優先（手元で素早く権限確認できる）。
 * Firebase 未設定なら常に DEV スタブにフォールバック。
 */
const USE_LIVE_AUTH =
  isFirebaseConfigured &&
  !!auth &&
  (import.meta.env.PROD || import.meta.env.VITE_AUTH_MODE === 'live');

// ── DEV プロバイダ（従来挙動・viewer は常に非null）──
function DevAuthProvider({ children }: { children: ReactNode }) {
  const [viewer, setViewerState] = useState<Member>(DEFAULT_DEV_VIEWER);

  // dev でも scope を viewer に追従させる（mockAdapter は無視するが、dev-auth +
  // firebase backend の検証時に絞り込みが効くようにする）。
  useEffect(() => {
    applyScope(viewer);
  }, [viewer]);

  const value = useMemo<AuthState>(
    () => ({
      viewer,
      uid: viewer.id, // DEV: viewer.id をオーバーレイ所有者キーに使う
      setViewer: (memberId: string) => {
        const next = MEMBER_MAP[memberId];
        if (next) setViewerState(next);
      },
      switchable: SWITCHABLE.length ? SWITCHABLE : MEMBERS,
      mode: 'dev',
      loading: false,
      unregisteredEmail: null,
      googleAccessToken: null, // DEV: 実トークンなし（Calendar連携は未接続表示）
      signInWithGoogle: async () => {
        /* DEV: no-op（切替UIで viewer を変える）*/
      },
      signOut: async () => {
        /* DEV: no-op */
      },
    }),
    [viewer],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * 初回ログイン時の権限doc自動プロビジョン（callable provisionMe）。
 *
 * 権限の正本は members/{uid}（Rules の getMember()）だが、seed は members/{安定id} で
 * キーされる。本 callable はサーバー側で members/{安定id} → members/{uid} を整え、
 * 手動 reconcile を不要にする。クライアントからは何も渡さない（権限を盛れない）。
 *
 * viewer 解決（resolveMemberByEmail＝members collection read）の **前段** で await し、
 * members/{uid} を用意してから email 照合に進む。失敗（ネットワーク等）は 1 回リトライ→
 * それでもダメなら握りつぶして既存挙動にフォールバック（落とさない）。
 *
 * functions 未設定（Firebase 未構成・DEV 相当）では呼ばない。
 */
async function provisionViewer(): Promise<void> {
  if (!functions) return; // Firebase/Functions 未設定 → スキップ（DEV/モック経路でも呼ばない）。
  const callProvision = httpsCallable<void, { status: string }>(functions, 'provisionMe');
  try {
    await callProvision();
  } catch {
    // 1 回だけリトライ。
    try {
      await callProvision();
    } catch {
      // プロビジョン不能でも viewer 解決は試みる（既存の email 照合挙動にフォールバック）。
      // 生値（uid/email）はログに出さない。
    }
  }
}

/** email（小文字・前後空白除去）で members を引き当てる。 */
async function resolveMemberByEmail(email: string | null): Promise<Member | null> {
  if (!email) return null;
  const key = email.trim().toLowerCase();
  const adapter = await getAdapter();
  const members = await adapter.getMembers();
  return members.find((m) => (m.email || '').trim().toLowerCase() === key) || null;
}

// ============================================================
// Google アクセストークンの永続化＋サーバー発行（LIVE モード専用）
// ============================================================

/** Calendar 読み取り（C2）＋ Tasks（C3）＋ Docs 読み取り（Meet議事録の取り込み）の3スコープ。
 *  手動「連携」= signInWithGoogle のポップアップで要求。
 *  ⚠️ drive.readonly は Google 審査を軽くするため除去した（2026-07-10・たいき承認）。
 *    Drive 一覧機能（旧C4・Projects.tsx の LiveDriveCard）は SHOW_DRIVE_FILES=false で無効化済み。
 *    復活には権限再追加＋CASA審査が必要。
 *  ⚠️ functions/src/oauth.ts の SYNC_SCOPES（B2 同意フロー＝サーバー発行トークンの scope）と
 *  **同一セットに揃えること**。片方だけ増やすと、サーバー発行トークンが scope 検査
 *  （needsConsent）で弾かれ続ける／API 呼び出しが 403 になる。
 *  Drive 系スコープ（drive / drive.file 等の書き込み系）は追加しない方針。 */
const GOOGLE_API_SCOPES = [
  'https://www.googleapis.com/auth/calendar.readonly',
  'https://www.googleapis.com/auth/tasks',
  'https://www.googleapis.com/auth/documents.readonly',
];

// ── (1) sessionStorage キャッシュ ──
//
// ⚠️ セキュリティ上の判断: アクセストークンを Web Storage に置くと XSS で読まれ得る。
// ここでは (a) 社内メンバー限定ツール (b) トークンが短命（実質 ≤55分で失効扱い）
// (c) sessionStorage はタブ単位・タブを閉じれば消える、の3点からリスク許容と判断。
// refresh_token 等の長命クレデンシャルは絶対に置かない。
const GOOGLE_TOKEN_CACHE_KEY = 'meeting-hub.googleAccessToken.v1';
/** Google access token は約3600秒有効。境界での 401 を避けるため安全側に 3300 秒で失効扱い。 */
const GOOGLE_TOKEN_TTL_MS = 3300 * 1000;

function readCachedGoogleToken(): string | null {
  try {
    const raw = sessionStorage.getItem(GOOGLE_TOKEN_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { token?: unknown; expiresAt?: unknown };
    if (typeof parsed.token !== 'string' || typeof parsed.expiresAt !== 'number') return null;
    if (Date.now() >= parsed.expiresAt) {
      sessionStorage.removeItem(GOOGLE_TOKEN_CACHE_KEY);
      return null;
    }
    return parsed.token;
  } catch {
    // ストレージ不可（プライベートモード等）・壊れた JSON は「キャッシュなし」扱い。
    return null;
  }
}

function cacheGoogleToken(token: string, ttlMs: number = GOOGLE_TOKEN_TTL_MS): void {
  try {
    sessionStorage.setItem(
      GOOGLE_TOKEN_CACHE_KEY,
      JSON.stringify({ token, expiresAt: Date.now() + ttlMs }),
    );
  } catch {
    // 保存できなくても動作は続行（次回リロードではサーバー発行（getGoogleAccessToken）で取り直す）。
  }
}

/** キャッシュ済みトークンの失効時刻（epoch ms）。無い・壊れている場合は null。
 *  失効タイマー（LiveAuthProvider）がサーバー再取得のタイミングを決めるのに使う。 */
function readCachedGoogleTokenExpiresAt(): number | null {
  try {
    const raw = sessionStorage.getItem(GOOGLE_TOKEN_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { expiresAt?: unknown };
    return typeof parsed.expiresAt === 'number' ? parsed.expiresAt : null;
  } catch {
    return null;
  }
}

function clearCachedGoogleToken(): void {
  try {
    sessionStorage.removeItem(GOOGLE_TOKEN_CACHE_KEY);
  } catch {
    /* no-op */
  }
}

// ── (2) サーバー発行トークン（callable getGoogleAccessToken）──
//   B2 常時同期でサーバー（Firestore userTokens・クライアント読取不可）に預かっている本人の
//   refresh_token から、Cloud Functions が access_token を発行して返す。
//   ポップアップ＝ユーザージェスチャが不要なので、リロード直後・別タブでも黙って復元できる。
//   ※ 旧実装の GIS（Google Identity Services）サイレント再取得は、ジェスチャ無しの
//     ポップアップが常にブロックされる（popup_failed_to_open・実測）ため構造的に機能せず削除。

/** サーバー発行トークンの安全マージン（秒）。expiresIn からこの分だけ早めに失効扱いする。 */
const SERVER_TOKEN_SAFETY_SEC = 300; // 5分

interface ServerTokenResponse {
  accessToken?: string;
  expiresIn?: number;
  scopes?: string;
  needsConsent?: boolean;
}

/**
 * callable `getGoogleAccessToken` で本人の Google アクセストークンをサーバーから取得する。
 *
 *   - 成功: { token, expiresInSec }
 *   - 'needsConsent': B2 未許可（refresh_token 未預託）／旧 scope（tasks のみ）／失効。
 *     → 呼び出し側は未連携表示のまま＝手動「連携」（signInWithGoogle）と MyTasks の
 *       「常時同期を有効にする」（startBackgroundSync）で同意し直してもらう。
 *   - null: 一時的な失敗（ネットワーク・未デプロイ等）。自動リトライはしない。
 *
 *   ⚠️ B2 を一度も許可していないユーザーは常に 'needsConsent'
 *      ＝「タブごとに連携ボタンを押す」従来体験のままになる（コード上の既知の制約）。
 */
async function requestGoogleTokenFromServer(): Promise<
  { token: string; expiresInSec: number } | 'needsConsent' | null
> {
  if (!functions) return null;
  try {
    const call = httpsCallable<void, ServerTokenResponse>(functions, 'getGoogleAccessToken');
    const res = await call();
    const data = res.data ?? {};
    if (data.needsConsent) return 'needsConsent';
    if (typeof data.accessToken === 'string' && data.accessToken) {
      return {
        token: data.accessToken,
        expiresInSec: typeof data.expiresIn === 'number' ? data.expiresIn : 3600,
      };
    }
    return null;
  } catch (err) {
    // callable 失敗（未デプロイ・ネットワーク等）。トークンや生値はログに出さない。
    console.warn(
      '[Auth] getGoogleAccessToken の呼び出しに失敗（未連携表示にフォールバック）',
      err instanceof Error ? err.message : err,
    );
    return null;
  }
}

// ── LIVE プロバイダ（Firebase Auth・Google）──
function LiveAuthProvider({ children }: { children: ReactNode }) {
  const [viewer, setViewerLive] = useState<Member | null>(null);
  const [uid, setUid] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [unregisteredEmail, setUnregisteredEmail] = useState<string | null>(null);
  // Google OAuth アクセストークン（Calendar/Tasks 用）。
  // 初期値は sessionStorage キャッシュから復元（リロード対策の (1)。未失効のみ）。
  // 取得経路・失効の詳細は AuthState.googleAccessToken のコメント参照。
  const [googleAccessToken, setGoogleAccessToken] = useState<string | null>(() =>
    readCachedGoogleToken(),
  );
  // サーバー発行トークンの取得はログイン確定ごとに1回だけ（失敗時の自動リトライはしない。
  // ただし失効タイマーが発火したときはリセットして取り直す）。
  const serverTokenAttemptedRef = useRef(false);

  useEffect(() => {
    if (!auth) return;
    let cancelled = false;
    const unsub = onAuthStateChanged(auth, async (fbUser) => {
      if (cancelled) return;
      // ハンドラ全体を try/catch/finally で守る：途中の await（provisionViewer /
      // resolveMemberByEmail）が予期せず throw しても loading が finally で必ず解除され、
      // 「読み込み中…」のまま固まらない（catch は未ログイン相当＝Login 表示へ倒す）。
      try {
        if (!fbUser) {
          setViewerLive(null);
          setUid(null);
          setUnregisteredEmail(null);
          // サインアウト／未ログイン時はトークンも破棄（キャッシュも残さない）。
          setGoogleAccessToken(null);
          clearCachedGoogleToken();
          // 次のログインで改めてサーバー発行トークンを試せるようにリセット。
          serverTokenAttemptedRef.current = false;
          applyScope(null);
          return;
        }
        // Auth uid はメンバー解決の成否に関わらず保持（オーバーレイ所有者キー）。
        setUid(fbUser.uid);
        // ⚠️ viewer 解決（members read）の前に provisionMe を await。members/{安定id} →
        //    members/{uid} をサーバー側で整え、初回ログインでも email 照合が通るようにする。
        //    失敗時は内部でフォールバック（落とさない）。loading は維持されたまま。
        await provisionViewer();
        if (cancelled) return;
        const member = await resolveMemberByEmail(fbUser.email);
        if (cancelled) return;
        if (member) {
          // ⚠️ scope は viewer 状態反映より前に同期で確定させる（screens の getTasks/
          //    getNotes が走る前に絞り込み scope が効いている必要があるため）。
          applyScope(member);
          setViewerLive(member);
          setUnregisteredEmail(null);
        } else {
          // ログイン成功したが members に未登録 → ガード表示
          applyScope(null);
          setViewerLive(null);
          setUnregisteredEmail(fbUser.email || '(不明)');
        }
      } catch (err) {
        // 想定外の失敗は未ログイン相当へ倒す（Login 画面から再試行できる）。
        // 秘密・email 生値はログに出さない（Error の message のみ）。
        console.warn(
          '[Auth] ログイン状態の解決に失敗しました（未ログイン表示にフォールバック）',
          err instanceof Error ? err.message : err,
        );
        applyScope(null);
        setViewerLive(null);
        setUnregisteredEmail(null);
      } finally {
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
      unsub();
    };
  }, []);

  // ── (2) サーバー発行トークンの取得 ──
  // ログイン確定後（loading 解除・viewer 確定）にトークンが無い＝(1) のキャッシュが
  // 空振り（リロード後の失効・別タブ・失効タイマー発火後）のとき、callable で
  // サーバーから取得する。needsConsent／失敗時は従来動作
  // （未連携表示→手動「連携」= signInWithGoogle）のまま。
  useEffect(() => {
    if (loading || !viewer || googleAccessToken) return;
    if (serverTokenAttemptedRef.current) return;
    serverTokenAttemptedRef.current = true;
    let cancelled = false;
    void (async () => {
      const result = await requestGoogleTokenFromServer();
      if (cancelled) return;
      if (result === 'needsConsent') {
        // B2 未許可・旧 scope・失効 → 未連携表示のまま。「連携」ボタン（signInWithGoogle・
        // prompt=consent）と MyTasks の常時同期許可（startBackgroundSync）で同意し直す。
        console.warn('[Auth] サーバー発行トークンは要再同意（未連携表示にフォールバック）');
        return;
      }
      if (!result) {
        console.warn('[Auth] サーバー発行トークンの取得に失敗（未連携表示にフォールバック）');
        return;
      }
      // expiresIn 準拠・安全側 −5分（最低60秒）でキャッシュして反映。
      const ttlMs = Math.max(result.expiresInSec - SERVER_TOKEN_SAFETY_SEC, 60) * 1000;
      cacheGoogleToken(result.token, ttlMs);
      setGoogleAccessToken(result.token);
    })();
    return () => {
      cancelled = true;
    };
  }, [loading, viewer, googleAccessToken]);

  // ── (3) 失効タイマー：キャッシュの有効期限が来たらトークンを破棄してサーバーから取り直す ──
  // サーバー発行はユーザージェスチャ不要なので、開きっぱなしのタブでも約55分ごとに
  // 黙って更新され続ける（旧実装はセッション中の失効後 401 のまま放置だった）。
  // 破棄 →（attempted をリセット）→ 上の (2) effect が再実行、の流れで取り直す。
  // B2 未許可ユーザーは (2) が needsConsent になり未連携表示へ落ちる（従来の失効時と同等）。
  useEffect(() => {
    if (!googleAccessToken) return;
    const expiresAt = readCachedGoogleTokenExpiresAt();
    // キャッシュに期限が無い（ストレージ不可等）場合は既定 TTL で見なす。
    const delay = Math.max((expiresAt ?? Date.now() + GOOGLE_TOKEN_TTL_MS) - Date.now(), 0);
    const timer = setTimeout(() => {
      clearCachedGoogleToken();
      serverTokenAttemptedRef.current = false; // (2) にもう一度取得させる
      setGoogleAccessToken(null);
    }, delay);
    return () => clearTimeout(timer);
  }, [googleAccessToken]);

  const value = useMemo<AuthState>(
    () => ({
      viewer,
      uid,
      // LIVE では viewer は実ログインで確定。切替UIは出さない。
      setViewer: () => {
        /* LIVE: no-op */
      },
      switchable: [],
      mode: 'live',
      loading,
      unregisteredEmail,
      googleAccessToken,
      signInWithGoogle: async () => {
        if (!auth) return;
        // サーバー発行（getGoogleAccessToken）と同一のスコープセット（GOOGLE_API_SCOPES）を要求。
        const provider = new GoogleAuthProvider();
        GOOGLE_API_SCOPES.forEach((scope) => provider.addScope(scope));
        // prompt=consent を強制：既ログインユーザーだと Google が同意画面をスキップし、
        // 新スコープ（documents.readonly 等）を含まない旧権限のままトークンを返すことがある
        // （2026-07-10 再連携ループの実バグ）。毎回同意画面を出して全スコープを確実に付与させる。
        provider.setCustomParameters({ prompt: 'consent' });
        const result = await signInWithPopup(auth, provider);
        // OAuth アクセストークンは popup の結果からのみ取得できる（onAuthStateChanged
        // では戻らない）。Calendar/Tasks API の Bearer トークンとして保持し、
        // リロード復元用に sessionStorage にもキャッシュする（(1)）。
        const token = GoogleAuthProvider.credentialFromResult(result)?.accessToken ?? null;
        if (token) {
          cacheGoogleToken(token);
        } else {
          clearCachedGoogleToken();
        }
        setGoogleAccessToken(token);
        // viewer 確定は onAuthStateChanged 経由
      },
      signOut: async () => {
        if (!auth) return;
        setGoogleAccessToken(null);
        clearCachedGoogleToken();
        await fbSignOut(auth);
      },
    }),
    [viewer, uid, loading, unregisteredEmail, googleAccessToken],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  return USE_LIVE_AUTH ? (
    <LiveAuthProvider>{children}</LiveAuthProvider>
  ) : (
    <DevAuthProvider>{children}</DevAuthProvider>
  );
}

/** 現在の閲覧ユーザーと認証APIを取得する。AuthProvider の外で呼ぶと throw。 */
export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>');
  return ctx;
}

/**
 * 確定済みの viewer（非null）を取得する。アプリシェル内（App.tsx で
 * ログインゲートを通過した後）でのみ使う。未確定なら throw する設計なので、
 * 各画面・コンポーネントは null チェックなしで viewer を扱える。
 */
export function useViewer(): Member {
  const { viewer } = useAuth();
  if (!viewer) {
    throw new Error('useViewer: viewer 未確定（ログインゲートの外で呼ばれた可能性）');
  }
  return viewer;
}
